import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BannerDTO } from '../../../interfaces/banner-management/banner-dto.interface';
import { BannerDisplayWeekEnum } from '../../../enums/banner-management/banner-display-week.enum';
import { ConfirmationService, SelectItem } from 'primeng';
import { BannerManagementService } from '../../../services/banner-management.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { _deepClone } from 'fast-json-patch/module/helpers';
import { compare } from 'fast-json-patch';
import { BannerDisplayTypeEnum } from '../../../enums/banner-management/banner-display-type.enum';
import { UserService } from 'src/app/services';

@Component({
  selector: 'app-banner-management-list',
  templateUrl: './banner-management-list.component.html',
  styleUrls: ['./banner-management-list.component.scss'],
})
export class BannerManagementListComponent implements OnInit {
  @Input() editMode: boolean;

  // filtered and sorted banners
  displayBanners: BannerDTO[];

  _banners: BannerDTO[];
  @Input('banners') set banners(value: BannerDTO[]) {
    this._banners = value;

    // sort according to current filter
    this.sortAndFilterBanners();
  }
  get banners(): BannerDTO[] {
    return this._banners;
  }

  @Input() upcomingBanners: BannerDTO[];

  @Output() reloadBanners = new EventEmitter<any>();
  @Output() editSelectedBanner = new EventEmitter<BannerDTO>();
  @Output() createNewBanner = new EventEmitter<any>();

  public BannerDisplayWeek = BannerDisplayWeekEnum;
  public isBannerAdministrator: boolean;

  currentDisplayWeek = BannerDisplayWeekEnum.Weekdays;
  selectedDisplayWeek;
  displayWeekOptions: SelectItem[] = [];

  constructor(
    private confirmationService: ConfirmationService,
    private bannerService: BannerManagementService,
    private userService: UserService,
  ) {}

  async ngOnInit() {
    this.displayWeekOptions.push({
      label: 'Weekdays',
      value: BannerDisplayWeekEnum.Weekdays,
      title: 'A list of your current active banners from Monday to Friday',
    });
    this.displayWeekOptions.push({
      label: 'Weekends',
      value: BannerDisplayWeekEnum.Weekends,
      title: 'A list of your current active banners from Saturday to Sunday',
    });

    this.selectedDisplayWeek = this.displayWeekOptions[0];
    this.isBannerAdministrator = await this.userService.isUserInRole('BADM');
  }

  sortAndFilterBanners() {
    if (!this.banners) return;

    this.displayBanners = this.banners.filter(
      (x) =>
        x.displayWeek === this.currentDisplayWeek || x.displayWeek === BannerDisplayWeekEnum.All,
    );

    if (this.currentDisplayWeek === BannerDisplayWeekEnum.Weekends) {
      this.displayBanners = this.displayBanners.sort(
        (a, b) => a.positionInCarouselWeekends - b.positionInCarouselWeekends,
      );
    } else {
      this.displayBanners = this.displayBanners.sort(
        (a, b) => a.positionInCarouselWeekdays - b.positionInCarouselWeekdays,
      );
    }
  }

  deleteBanner(banner: BannerDTO) {
    const msg = `Are you sure you want to delete “${banner.title}”? Once this is deleted, any banners below will move up one position when displayed in app.`;

    this.confirmationService.confirm({
      message: msg,
      header: 'Delete Banner?',
      accept: () => {
        this.deleteConfirmed(banner);
      },
    });
  }

  private deleteConfirmed(banner: BannerDTO) {
    this.bannerService.deleteBanner(banner.id).then((res) => {
      if (res) {
        // raise output event to reload banners
        this.reloadBanners.emit();
      }
    });
  }

  editBanner(banner: BannerDTO) {
    this.editSelectedBanner.emit(banner);
  }

  createBanner() {
    this.createNewBanner.emit();
  }

  async onBannerDropped(data: CdkDragDrop<BannerDTO[]>) {
    // change the weekday/weekend position according to data
    moveItemInArray(this.displayBanners, data.previousIndex, data.currentIndex);

    // iterate the array and modify the position values according to current filter view
    await this.resetBannerIndexes(this.displayBanners);
    this.sortAndFilterBanners();
  }

  private async resetBannerIndexes(banners: BannerDTO[]) {
    let weekdayIndex = 1;
    let weekendIndex = 1;

    for (const b of banners) {
      const bTemp = _deepClone(b);

      if (this.currentDisplayWeek === BannerDisplayWeekEnum.Weekdays) {
        b.positionInCarouselWeekdays = weekdayIndex++;
      } else if (this.currentDisplayWeek === BannerDisplayWeekEnum.Weekends) {
        b.positionInCarouselWeekends = weekendIndex++;
      }

      // patch the record via the API
      const patch = compare(bTemp, b);

      await this.bannerService.patchBanner(b.id, patch);
    }
  }

  onDisplayWeekChange(data: any) {
    //console.log('Selected display week: ', data.value);
    this.selectedDisplayWeek = this.displayWeekOptions[this.currentDisplayWeek - 1];

    // sort according to current filter
    this.sortAndFilterBanners();
  }

  statusClass(status) {
    return `status-${status}`;
  }
}
